<template>
    <template v-if="isRecommendationEnabled">
        <ClientOnly>
            <VJoyDialogTrigger v-if="hasAlreadyRecommendedThisProfile" :dialog="removeOwnDialog">
                <VJoyButton
                    :icon="deletionIcon"
                    data-testid="recommendation-own-delete"
                    size="small"
                    variant="ghost"
                    @mouseout="hideDeleteRecommendationOption"
                    @mouseover="showDeleteRecommendationOption"
                >
                    {{ $t('profile.section.recommendation.cta.done.label', {0: profile?.firstname}) }}
                </VJoyButton>
            </VJoyDialogTrigger>
            <VJoyButton
                v-else
                class="recommendation-cta"
                data-testid="add-recommendation"
                icon="add"
                size="small"
                variant="ghost"
                @click="openAddRecommendation"
            >
                {{ $t('profile.section.recommendation.cta.label', [profile?.firstname]) }}
            </VJoyButton>
            <template #fallback>
                <SectionActionSkeleton />
            </template>
        </ClientOnly>
        <VJoyDialog
            id="removalOwnRecommendationConfirmDialog"
            ref="removeOwnDialog"
            :cancel-text="$t('general.cancel')"
            :confirm-text="$t('general.continue')"
            pattern-confirm
        >
            <template #dialog-header>
                <p>{{ $t('generic.form.confirm.text') }}</p>
            </template>

            <template #dialog-confirm>
                <VJoyButton data-testid="remove-own-recommendation-confirm" size="small" variant="main" @click="deleteOwnRecommendationAction">
                    {{ $t('general.continue') }}
                </VJoyButton>
            </template>
        </VJoyDialog>
    </template>
    <ClientOnly v-if="isTranslationEnabled">
        <TranslateButton section="recommendation" />
        <template #fallback>
            <SectionActionSkeleton />
        </template>
    </ClientOnly>
</template>

<script lang="ts" setup>
    import {computed, onMounted, onUnmounted, ref} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useGuestHost, navigateTo, useAuth, useProfilePageViewMode, useRoute} from '#imports';
    import {VJoyButton, VJoyDialog, VJoyDialogTrigger} from '@maltjoy/core-vue';
    import TranslateButton from '~/components/main/TranslateButton.vue';
    import SectionActionSkeleton from '~/components/section/SectionActionSkeleton.vue';
    import {useProfileTracking} from '~/composables/useProfileTracking';
    import {useExtractQueryParameters} from '~/composables/useUrlUtils';
    import {anchorsPageProfile} from '~/config/anchors';
    import {eventBus} from '~/config/events';
    import {RecommendationDeletedBy} from '~/config/TrackingEvents';
    import {useRecommendationsStore} from '~/features/recommendations/store/recommendations.store';
    import {useProfileStore} from '~/store/profileStore';
    import {TranslationDirection, useTranslationStore} from '~/store/translation/translationStore';

    // Stores
    const {loggedIn} = useAuth();
    const profileStore = useProfileStore();
    const {profile} = storeToRefs(profileStore);
    const recommendationsStore = useRecommendationsStore();
    const {recommendations} = storeToRefs(recommendationsStore);
    const {isRecommendationEnabled, isTranslationEnabled} = useProfilePageViewMode();
    const {translateAll, replaceTranslationContent} = useTranslationStore();
    const {trackRecommendationDeleted} = useProfileTracking();

    // Refs
    const deletionOption = ref(false);
    const removeOwnDialog = ref<InstanceType<typeof VJoyDialog> | null>(null);

    // Computed values
    const deletionIcon = computed(() => (deletionOption.value ? 'trash' : 'check'));
    const hasAlreadyRecommendedThisProfile = computed(() =>
        recommendations.value?.recommendations.some((reco) => reco.links.some((hateoasLink: any) => hateoasLink.rel === 'deleteRecommendation')),
    );

    const translationSection = 'recommendations';
    const translationField = 'textContent';

    onMounted(() => {
        eventBus.on('translation:request', translateRecommendations);
        eventBus.on('translation:restore', restoreRecommendations);
    });

    onUnmounted(() => {
        eventBus.off('translation:request', translateRecommendations);
        eventBus.off('translation:restore', restoreRecommendations);
    });

    /**
     * Delete own recommendation
     */
    function deleteOwnRecommendationAction() {
        const recommendationToDelete = recommendations.value?.recommendations.find((reco) =>
            reco.links.some((hateoasLink: any) => hateoasLink.rel === 'deleteRecommendation'),
        );
        if (!recommendationToDelete) {
            return;
        }
        recommendationsStore.deleteRecommendation(recommendationToDelete);
        trackRecommendationDeleted(profile.value!.profileId, RecommendationDeletedBy.AUTHOR, recommendationToDelete.experienceId);
        removeOwnDialog.value?.hide();
    }

    function showDeleteRecommendationOption() {
        deletionOption.value = true;
    }

    function hideDeleteRecommendationOption() {
        deletionOption.value = false;
    }

    /**
     * Open add recommendation dialog
     */
    async function openAddRecommendation() {
        if (!loggedIn) {
            const route = useRoute();
            const host = useGuestHost();

            const queryParams = new URLSearchParams(useExtractQueryParameters(route));
            queryParams.set('drawerOpen', 'true');

            const redirectUriParams = encodeURIComponent(`${location.pathname}?${queryParams}#${anchorsPageProfile.recommendationsSection}`);
            const redirectUri = `${host}/signin?redirect=${redirectUriParams}`;
            await navigateTo(redirectUri, {external: true});
        }
        eventBus.emit('external-action:recommendations:add');
    }

    /**
     * Translate recommendations
     */
    async function translateRecommendations() {
        const recommendationTextContentRequests =
            recommendations.value?.recommendations.map((recommendation, index) => {
                return {
                    section: translationSection,
                    field: translationField,
                    index,
                    originalContent: recommendation.textContent ?? ' ',
                };
            }) || [];

        await translateAll(recommendationTextContentRequests);
        replaceTranslationContent(translationSection, translationField, TranslationDirection.TRANSLATE, switchTranslation);
    }
    function restoreRecommendations() {
        replaceTranslationContent(translationSection, translationField, TranslationDirection.RESTORE, switchTranslation);
    }
    function switchTranslation(content: string, index: number) {
        recommendations.value!.recommendations[index].textContent = content;
    }
</script>
